import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { InputHTMLAttributes } from 'react';
import { formatMoney, formatMoneyTyping, parseMoney } from '../../utils/money';

export interface MoneyInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  /** The amount as a number (or numeric string). `undefined`/`null`/`''` shows an empty field. */
  value?: number | string | null;
  /** Called with a plain number (or `undefined` when cleared), never formatted text. */
  onValueChange: (value: number | undefined) => void;
}

const asNumber = (value?: number | string | null): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};
const show = (value?: number | string | null) => {
  const n = asNumber(value);
  return n === undefined ? '' : formatMoney(n);
};

/**
 * The app's amount field: shows 1,234,567.50 while the user types and on blur,
 * but hands its parent a plain number. Use this for every editable amount.
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  { value, onValueChange, onBlur, className = 'input w-full', placeholder = '0.00', ...rest },
  forwardedRef,
) {
  const [text, setText] = useState(() => show(value));
  const inputRef = useRef<HTMLInputElement | null>(null);
  const caretDigits = useRef<number | null>(null);

  // Follow external changes (e.g. rent auto-filled from the chosen unit) without
  // fighting the user's own typing: only resync when the number actually differs.
  useEffect(() => {
    if (parseMoney(text) !== asNumber(value)) setText(show(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Keep the caret next to the same digit after separators are re-inserted.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el || caretDigits.current === null || document.activeElement !== el) return;
    let digits = caretDigits.current;
    let pos = 0;
    while (pos < text.length && digits > 0) {
      if (/[\d.]/.test(text[pos])) digits--;
      pos++;
    }
    el.setSelectionRange(pos, pos);
    caretDigits.current = null;
  }, [text]);

  return (
    <input
      {...rest}
      ref={(el) => {
        inputRef.current = el;
        if (typeof forwardedRef === 'function') forwardedRef(el);
        else if (forwardedRef) forwardedRef.current = el;
      }}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      className={className}
      placeholder={placeholder}
      value={text}
      onChange={(e) => {
        const raw = e.target.value;
        const caret = e.target.selectionStart ?? raw.length;
        caretDigits.current = raw.slice(0, caret).replace(/[^\d.]/g, '').length;
        const next = formatMoneyTyping(raw);
        setText(next);
        onValueChange(parseMoney(next));
      }}
      onBlur={(e) => {
        const n = parseMoney(text);
        setText(n === undefined ? '' : formatMoney(n));
        onBlur?.(e);
      }}
    />
  );
});
